// api-tokens.js — Settings → API Tokens panel: create / edit / roll /
// delete, scope picker, expiry handling, full-access (owner-only)
// escape hatch.
//
// Tied tightly to the static HTML in index.html: each modal +
// reveal-after-create + edit-drawer DOM element has fixed IDs that
// this module reads/writes. The handlers are all installed as
// delegated listeners at module init (one keydown, one click, one
// change on document) — re-running this module is harmless.
//
// renderApiTokens is exported and re-exposed on AC_LEGACY by bootstrap
// so pages/settings.js can trigger a re-render from its mount path.
//
// Scope vocabulary mirrors internal/auth/scopes.go and the worker's
// VALID_SCOPES set (see infra/workers/coordinator.ts). 26 fine-
// grained scopes grouped into 9 sections (was 21 + 8 pre-migration
// 037 — added 5 page-level read scopes), plus the `*` full-access
// escape hatch.
//
// gate values:
//   ""       — any signed-in user can grant
//   "admin"  — requires user_teams.role = admin (or legacy "owner"
//              from rows that pre-date migration 035)
//
// Keep this in sync with the Go/TS scope tables; a desync here grants
// UI checkboxes the backend will reject (annoying, not a security
// hole — the server is the authority).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2g).
import { $ } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { showToast, showConfirm } from "./ui.js";
import { api, apiAsUser } from "./api.js";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

const SCOPE_SECTIONS = [
  { title: 'Activity & events', scopes: [
    { id: 'read:activity',  gate: '',      desc: 'List leases, ledger, members' },
    { id: 'write:activity', gate: '',      desc: 'Acquire / release leases, heartbeat' },
    { id: 'read:events',    gate: '',      desc: 'Replay past events' },
    { id: 'stream:events',  gate: '',      desc: 'Subscribe to live event stream' },
  ]},
  { title: 'Policy & rules',    scopes: [
    { id: 'read:policy',    gate: '',      desc: 'Read decision rules and templates' },
    { id: 'admin:policy',   gate: 'admin', desc: 'Mutate rules, overrides, dangerous-paths' },
  ]},
  { title: 'Settings',          scopes: [
    { id: 'read:settings',  gate: '',      desc: 'Read team settings' },
    { id: 'admin:settings', gate: 'admin', desc: 'Change team settings' },
  ]},
  { title: 'Members & team',    scopes: [
    { id: 'read:members',   gate: '',      desc: 'List members and invites' },
    { id: 'admin:members',  gate: 'admin', desc: 'Invite, remove, change roles' },
    { id: 'admin:team',     gate: 'admin', desc: 'Rename / delete team' },
  ]},
  { title: 'Worktrees',         scopes: [
    { id: 'read:worktrees',  gate: '',     desc: 'List worktrees' },
    { id: 'write:worktrees', gate: '',     desc: 'Create / archive worktrees' },
  ]},
  { title: 'Spend & billing',   scopes: [
    { id: 'read:spend',     gate: '',      desc: 'Read spend ledger and caps' },
    { id: 'admin:spend',    gate: 'admin', desc: 'Change spend caps' },
    { id: 'admin:billing',  gate: 'admin', desc: 'Manage subscription and payment' },
  ]},
  { title: 'Webhooks',          scopes: [
    { id: 'read:webhooks',  gate: '',      desc: 'List webhooks' },
    { id: 'admin:webhooks', gate: 'admin', desc: 'Create / rotate / delete webhooks' },
  ]},
  { title: 'Messaging',         scopes: [
    { id: 'read:messaging',  gate: '',     desc: 'Read messages and threads' },
    { id: 'write:messaging', gate: '',     desc: 'Send messages, broadcasts' },
  ]},
  { title: 'Audit',             scopes: [
    { id: 'read:audit',     gate: '',      desc: 'Read audit log' },
  ]},
  { title: 'Pages',             scopes: [
    { id: 'read:overview',  gate: '',     desc: 'View Overview / activity-stream home' },
    { id: 'read:chats',     gate: '',     desc: 'View active AI chat sessions' },
    { id: 'read:files',     gate: '',     desc: 'View cross-chat file activity' },
    { id: 'read:analytics', gate: '',     desc: 'View LLM spend + decision-rate analytics' },
    { id: 'read:agents',    gate: '',     desc: 'View per-session agent inspector' },
  ]},
  // Account section retired in migration 036 — read:account /
  // admin:account scopes no longer exist. The Account page is
  // self-service and always accessible.
];
const DEFAULT_CHECKED_SCOPES = new Set(['read:activity', 'write:activity']);

// Resolved user_teams.role for the signed-in dashboard user. Cached
// in module scope so repeated picker renders don't refetch. Refreshed
// each time the create form opens. Falls back to "" if no user
// session or network error — the picker then hides admin/owner scopes
// entirely.
let userTeamRole = '';
let userTeamTier = ''; // "solo" / "startup" / "enterprise" — drives the Solo-tier upgrade nudge

async function refreshUserTeamRole() {
  try {
    const info = await api('/v1/teams/info');
    userTeamRole = (info && info.your_team_role) || '';
    userTeamTier = (info && info.tier) || '';
  } catch { userTeamRole = ''; userTeamTier = ''; }
}

function satisfiesGate(gate) {
  if (gate === '') return true;
  // Owner role retired in migration 035 — userTeamRole === 'owner'
  // kept as back-compat for JWTs minted pre-migration.
  if (gate === 'admin') return userTeamRole === 'admin' || userTeamRole === 'owner';
  return false;
}

// applyFullAccessDim mirrors the change-handler effect for the
// full-access toggle: when ON, dim the scope-grid checkboxes and
// disable them all; when OFF, restore the original role-gated state.
// Called both from the change handler AND from open-form code paths
// that programmatically set fullCb.checked (programmatic assignment
// doesn't fire 'change'). Without this, the edit drawer for a *-token
// would show all scope checkboxes enabled despite the full-access
// toggle being checked.
function applyFullAccessDim(pickerEl, isFullAccess) {
  if (!pickerEl) return;
  pickerEl.style.opacity = isFullAccess ? '0.4' : '';
  pickerEl.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
    if (cb.dataset.origDisabled === undefined) {
      cb.dataset.origDisabled = cb.disabled ? '1' : '';
    }
    cb.disabled = isFullAccess || cb.dataset.origDisabled === '1';
  });
}

// renderScopePicker fills a container element with the 26-scope grid.
// selected: Set<string> of scopes that should start checked.
// namePrefix: lets us reuse the same grid for both create + edit
// forms without colliding ID/name attributes.
function renderScopePicker(container, selected, namePrefix) {
  if (!container) return;
  const sections = SCOPE_SECTIONS.map((sec) => {
    const items = sec.scopes.map((s) => {
      const allowed = satisfiesGate(s.gate);
      const isChecked = selected.has(s.id);
      const lockHint = !allowed
        ? ' <span style="color: var(--text-tertiary); font-size: 11px;">(admin only)</span>'
        : '';
      return `<label style="display: flex; align-items: flex-start; gap: 8px; cursor: ${allowed ? 'pointer' : 'not-allowed'}; font-size: 13px; padding: 3px 0; ${!allowed ? 'opacity: 0.55;' : ''}">
        <input type="checkbox" value="${escapeHTML(s.id)}" data-scope-cb="${namePrefix}" ${isChecked ? 'checked' : ''} ${!allowed ? 'disabled' : ''} />
        <span><code style="font-family: var(--font-mono); font-size: 12px; color: var(--text);">${escapeHTML(s.id)}</code> &mdash; <span style="color: var(--text-secondary);">${escapeHTML(s.desc)}</span>${lockHint}</span>
      </label>`;
    }).join('');
    return `<div style="margin-bottom: var(--s3);">
      <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">${escapeHTML(sec.title)}</div>
      ${items}
    </div>`;
  }).join('');
  container.innerHTML = sections;
}

export async function renderApiTokens() {
  const list = document.getElementById('api-tokens-list');
  if (!list) return;
  // API tokens require a user session (not a laptop team-token),
  // since they're scoped to "me". If the user is authenticated via a
  // laptop token only, show a friendly prompt instead of a backend
  // error.
  const supabase = getSupabase();
  if (!supabase) { list.innerHTML = ''; return; }
  let hasUserSession = false;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    hasUserSession = !!session;
  } catch {}
  if (!hasUserSession) {
    list.innerHTML = '<div style="padding: var(--s5); text-align: center; color: var(--text-tertiary); font-size: 13px; line-height: 1.6;">API tokens require a user account. Sign in with email and password (not a CLI team token) to create and manage integration tokens.</div>';
    return;
  }
  try {
    const data = await apiAsUser('/v1/me/api-tokens');
    const tokens = data.tokens || [];
    if (tokens.length === 0) {
      list.innerHTML = '<div style="padding: var(--s5); text-align: center; color: var(--text-tertiary); font-size: 13px;">No API tokens yet. Create one to get started with integrations.</div>';
      return;
    }
    list.innerHTML = tokens.map((t) => {
      const scopesArr = Array.isArray(t.scopes) ? t.scopes : [];
      const scopesStr = scopesArr.length === 0
        ? '<span style="color: var(--text-tertiary);">no scopes</span>'
        : (scopesArr.length === 1 && scopesArr[0] === '*'
            ? '<strong style="color: var(--danger);">full access (*)</strong>'
            : escapeHTML(scopesArr.join(', ')));
      const exp = t.expires_at ? ('expires ' + new Date(t.expires_at).toLocaleDateString()) : '<span style="color: var(--warning);">never expires</span>';
      const last = t.last_used_at ? ('last used ' + ago(t.last_used_at)) : 'never used';
      return `<div style="padding: var(--s3) var(--s5); border-bottom: 1px solid var(--border); display: flex; align-items: flex-start; justify-content: space-between; gap: var(--s3);">
        <div style="min-width: 0; flex: 1;">
          <div style="font-weight: 600; font-size: 14px; color: var(--text);">${escapeHTML(t.name)}</div>
          <div style="font-family: var(--font-mono); font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">${escapeHTML(t.token_prefix)}&bull;&bull;&bull;&bull;&bull;&bull; &middot; ${scopesStr}</div>
          <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">${exp} &middot; ${last}</div>
        </div>
        <div style="display: flex; gap: var(--s2); flex-shrink: 0;">
          <button class="btn btn-sm" data-api-tok-edit="${escapeHTML(t.id)}" style="font-size: 11px; padding: 3px 10px;">Edit</button>
          <button class="btn btn-sm" data-api-tok-roll="${escapeHTML(t.id)}" style="font-size: 11px; padding: 3px 10px;">Roll</button>
          <button class="btn btn-sm" data-api-tok-delete="${escapeHTML(t.id)}" style="font-size: 11px; padding: 3px 10px; color: var(--danger); border-color: var(--danger);">Delete</button>
        </div>
      </div>`;
    }).join('');
  } catch (e) {
    list.innerHTML = '<div style="padding: var(--s4); color: var(--danger); font-size: 13px;">Failed to load: ' + escapeHTML(e.message || 'unknown') + '</div>';
  }
}

async function showApiTokenCreateForm() {
  const backdrop = document.getElementById('api-token-create-backdrop');
  if (!backdrop) return;
  // Refresh role + tier first so we can intercept Solo before showing
  // the form. Without this gate the user would fill out the form, hit
  // submit, and bounce off the worker's 402 — frustrating UX.
  await refreshUserTeamRole();
  if (userTeamTier === 'solo') {
    showToast('info', 'API tokens require a Startup or Enterprise plan. Visit the Billing tab to upgrade.');
    return;
  }
  renderScopePicker(
    document.getElementById('api-token-scope-picker'),
    new Set(DEFAULT_CHECKED_SCOPES),
    'create'
  );
  // Admin-only `*` full-access toggle (was owner-only pre-migration 035).
  const advanced = document.getElementById('api-token-scope-advanced');
  if (advanced) advanced.style.display = (userTeamRole === 'admin' || userTeamRole === 'owner') ? '' : 'none';
  const fullCb = document.getElementById('api-token-scope-fullaccess');
  if (fullCb) fullCb.checked = false;
  applyFullAccessDim(document.getElementById('api-token-scope-picker'), false);
  backdrop.classList.add('open');
  // Defer focus so the modal's transition completes before the input
  // tries to claim focus (avoids a brief scroll-jump on slower browsers).
  setTimeout(() => {
    const nameEl = document.getElementById('api-token-name');
    if (nameEl) nameEl.focus();
  }, 50);
}

function hideApiTokenCreateForm() {
  const backdrop = document.getElementById('api-token-create-backdrop');
  if (backdrop) backdrop.classList.remove('open');
  const nameEl = document.getElementById('api-token-name');
  if (nameEl) nameEl.value = '';
  const r90 = document.querySelector('input[name="api-token-exp"][value="90"]');
  if (r90) r90.checked = true;
  const warn = document.getElementById('api-token-never-warning');
  if (warn) warn.style.display = 'none';
  const fullCb = document.getElementById('api-token-scope-fullaccess');
  if (fullCb) fullCb.checked = false;
}

async function submitApiTokenCreate() {
  const name = document.getElementById('api-token-name').value.trim();
  if (!name) { showToast('error', 'Please give the token a name.'); return; }

  // Full-access escape hatch: confirm + send `["*"]` alone.
  const fullCb = document.getElementById('api-token-scope-fullaccess');
  let scopes;
  if (fullCb && fullCb.checked) {
    const ok = await showConfirm(
      'Create a full-access token?',
      'This token will be granted every scope, including ones added in the future. Only do this if scoped access truly won’t work for your integration.',
      'Create full-access token'
    );
    if (!ok) return;
    scopes = ['*'];
  } else {
    scopes = Array.from(document.querySelectorAll('[data-scope-cb="create"]:checked')).map((c) => c.value);
    if (scopes.length === 0) { showToast('error', 'Pick at least one scope.'); return; }
  }

  const expChoice = document.querySelector('input[name="api-token-exp"]:checked').value;
  let expiresAt = null;
  if (expChoice === 'never') { expiresAt = null; }
  else if (expChoice === 'custom') {
    const date = document.getElementById('api-token-custom-date').value;
    if (!date) { showToast('error', 'Pick a custom date.'); return; }
    expiresAt = new Date(date + 'T23:59:59Z').toISOString();
  } else {
    const days = parseInt(expChoice, 10);
    expiresAt = new Date(Date.now() + days * 86400000).toISOString();
  }

  const btn = document.getElementById('api-token-create-submit');
  btn.disabled = true; btn.textContent = 'Creating...';
  try {
    const res = await apiAsUser('/v1/me/api-tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scopes, expires_at: expiresAt }),
    });
    hideApiTokenCreateForm();
    document.getElementById('api-token-revealed').style.display = '';
    document.getElementById('api-token-revealed-value').textContent = res.token;
    renderApiTokens();
  } catch (err) {
    showToast('error', 'Failed to create token: ' + (err.message || 'unknown'));
  } finally {
    btn.disabled = false; btn.textContent = 'Create Token';
  }
}

// ---- Edit drawer ----

async function showApiTokenEditForm(id) {
  const form = document.getElementById('api-token-edit-form');
  if (!form) return;
  // Fetch the row to seed name + scopes. Re-using the list endpoint
  // is simpler than adding a single-token GET that the backend
  // doesn't expose yet.
  let row = null;
  try {
    const data = await apiAsUser('/v1/me/api-tokens');
    row = (data.tokens || []).find((t) => t.id === id);
  } catch {}
  if (!row) { showToast('error', 'Couldn’t load that token.'); return; }

  await refreshUserTeamRole();

  form.dataset.editingId = id;
  document.getElementById('api-token-edit-name').value = row.name || '';

  const scopes = Array.isArray(row.scopes) ? row.scopes : [];
  const hasFull = scopes.length === 1 && scopes[0] === '*';
  renderScopePicker(
    document.getElementById('api-token-edit-scope-picker'),
    new Set(hasFull ? [] : scopes),
    'edit'
  );
  const advanced = document.getElementById('api-token-edit-scope-advanced');
  if (advanced) advanced.style.display = (userTeamRole === 'admin' || userTeamRole === 'owner') ? '' : 'none';
  const fullCb = document.getElementById('api-token-edit-scope-fullaccess');
  if (fullCb) fullCb.checked = hasFull;
  applyFullAccessDim(document.getElementById('api-token-edit-scope-picker'), hasFull);

  // Default the expiry radio to "keep current".
  const keep = document.querySelector('input[name="api-token-edit-exp"][value="keep"]');
  if (keep) keep.checked = true;

  form.style.display = '';
  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function hideApiTokenEditForm() {
  const form = document.getElementById('api-token-edit-form');
  if (!form) return;
  form.style.display = 'none';
  form.dataset.editingId = '';
}

async function submitApiTokenEdit() {
  const form = document.getElementById('api-token-edit-form');
  const id = form && form.dataset.editingId;
  if (!id) return;

  const name = document.getElementById('api-token-edit-name').value.trim();
  if (!name) { showToast('error', 'Name can’t be empty.'); return; }

  const fullCb = document.getElementById('api-token-edit-scope-fullaccess');
  let scopes;
  if (fullCb && fullCb.checked) {
    const ok = await showConfirm(
      'Switch this token to full access?',
      'It will be granted every scope, including ones added in the future. Only do this if scoped access truly won’t work.',
      'Use full access'
    );
    if (!ok) return;
    scopes = ['*'];
  } else {
    scopes = Array.from(document.querySelectorAll('[data-scope-cb="edit"]:checked')).map((c) => c.value);
    if (scopes.length === 0) { showToast('error', 'Pick at least one scope.'); return; }
  }

  // Expiry is a separate operation — `keep` leaves expires_at off the
  // PATCH body; 30d/90d use the /extend endpoint; custom + never go
  // through PATCH with expires_at.
  const expChoice = document.querySelector('input[name="api-token-edit-exp"]:checked').value;

  // Validate expiry inputs BEFORE issuing any network calls —
  // otherwise a missing custom date would land the name+scopes PATCH
  // and then bail, leaving the form in a confusing half-saved state.
  let customExpIso = null;
  if (expChoice === 'custom') {
    const date = document.getElementById('api-token-edit-custom-date').value;
    if (!date) { showToast('error', 'Pick a custom date.'); return; }
    customExpIso = new Date(date + 'T23:59:59Z').toISOString();
  }

  const btn = document.getElementById('api-token-edit-submit');
  btn.disabled = true; btn.textContent = 'Saving...';
  try {
    // 1. PATCH name + scopes (always — those are what the user is editing).
    await apiAsUser('/v1/me/api-tokens/' + id, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scopes }),
    });
    // 2. Apply expiry change separately (if any).
    if (expChoice === '30d' || expChoice === '90d') {
      await apiAsUser('/v1/me/api-tokens/' + id + '/extend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ by: expChoice }),
      });
    } else if (expChoice === 'custom') {
      await apiAsUser('/v1/me/api-tokens/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_at: customExpIso }),
      });
    } else if (expChoice === 'never') {
      await apiAsUser('/v1/me/api-tokens/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_at: null }),
      });
    }
    hideApiTokenEditForm();
    renderApiTokens();
    showToast('success', 'Token updated.');
  } catch (err) {
    showToast('error', 'Save failed: ' + (err.message || 'unknown'));
  } finally {
    btn.disabled = false; btn.textContent = 'Save changes';
  }
}

// ---- module init: delegated click + change + Esc handlers ----

function wireListeners() {
  document.addEventListener('click', async (e) => {
    if (e.target.id === 'api-token-create-btn') { showApiTokenCreateForm(); return; }
    if (e.target.id === 'api-token-create-cancel') { hideApiTokenCreateForm(); return; }
    if (e.target.id === 'api-token-create-submit') { submitApiTokenCreate(); return; }
    // Backdrop click (outside the modal box) dismisses the modal.
    // Matches the dismiss-on-backdrop pattern used by showConfirm /
    // showPrompt elsewhere.
    if (e.target.id === 'api-token-create-backdrop') { hideApiTokenCreateForm(); return; }
    if (e.target.id === 'api-token-edit-cancel') { hideApiTokenEditForm(); return; }
    if (e.target.id === 'api-token-edit-submit') { submitApiTokenEdit(); return; }
    if (e.target.id === 'api-token-revealed-close') {
      document.getElementById('api-token-revealed').style.display = 'none';
      document.getElementById('api-token-revealed-value').textContent = '';
      return;
    }
    if (e.target.id === 'api-token-revealed-copy') {
      const val = document.getElementById('api-token-revealed-value').textContent;
      try { await navigator.clipboard.writeText(val); e.target.textContent = 'Copied!'; setTimeout(() => { e.target.textContent = 'Copy'; }, 1500); } catch {}
      return;
    }
    if (e.target.dataset && e.target.dataset.apiTokEdit) {
      showApiTokenEditForm(e.target.dataset.apiTokEdit);
      return;
    }
    if (e.target.dataset && e.target.dataset.apiTokDelete) {
      const id = e.target.dataset.apiTokDelete;
      const ok = await showConfirm(
        'Delete this token?',
        'Any service using it will stop working immediately. This can’t be undone.',
        'Delete'
      );
      if (!ok) return;
      try { await apiAsUser('/v1/me/api-tokens/' + id, { method: 'DELETE' }); renderApiTokens(); showToast('success', 'Token deleted.'); }
      catch (err) { showToast('error', 'Delete failed: ' + (err.message || 'unknown')); }
      return;
    }
    if (e.target.dataset && e.target.dataset.apiTokRoll) {
      const id = e.target.dataset.apiTokRoll;
      const ok = await showConfirm(
        'Roll this token?',
        'The old value will stop working immediately. You’ll need to update any service using it.',
        'Roll token'
      );
      if (!ok) return;
      try {
        const res = await apiAsUser('/v1/me/api-tokens/' + id + '/roll', { method: 'POST' });
        document.getElementById('api-token-revealed').style.display = '';
        document.getElementById('api-token-revealed-value').textContent = res.token;
        renderApiTokens();
      } catch (err) { showToast('error', 'Roll failed: ' + (err.message || 'unknown')); }
      return;
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target && e.target.name === 'api-token-exp') {
      const warn = document.getElementById('api-token-never-warning');
      if (warn) warn.style.display = e.target.value === 'never' ? '' : 'none';
    }
    // Full-access toggle: dim picker + disable scope checkboxes when
    // ON; restore them when OFF. See applyFullAccessDim() for the
    // dataset-origDisabled stash that preserves role-gated state
    // across toggles.
    if (e.target && (e.target.id === 'api-token-scope-fullaccess' || e.target.id === 'api-token-edit-scope-fullaccess')) {
      const pickerId = e.target.id === 'api-token-scope-fullaccess' ? 'api-token-scope-picker' : 'api-token-edit-scope-picker';
      applyFullAccessDim(document.getElementById(pickerId), e.target.checked);
    }
  });

  // Esc closes the API-token-create modal. Matches the dismiss-on-Esc
  // behavior of showConfirm / showPrompt elsewhere in the app.
  //
  // Guard: only fire when there's NO showConfirm / showPrompt open on
  // top of us. Without this, hitting Esc to dismiss a "Create full-
  // access token?" confirm dialog would ALSO dismiss the underlying
  // create modal — user loses their form state to a single keystroke.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const confirmOpen = document.getElementById('confirm-backdrop')?.classList.contains('open');
    const promptOpen = document.getElementById('prompt-backdrop')?.classList.contains('open');
    if (confirmOpen || promptOpen) return; // a child modal owns this Esc
    const backdrop = document.getElementById('api-token-create-backdrop');
    if (backdrop && backdrop.classList.contains('open')) {
      hideApiTokenCreateForm();
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
